import {
  extractUserPersonalInterestsArray,
  mapPersonalInterestsToCategoryIds,
  parseUserHealthInterestIds,
} from "./userInterestsApi";

const STORAGE_PREFIX = "mytara_health_interests_v1";
export const HEALTH_INTERESTS_SESSION_DISMISS_KEY = "mytara_health_interests_session_dismiss";

export const getCurrentUserId = () => {
  const raw =
    localStorage.getItem("userData") || sessionStorage.getItem("userData");
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    const id = data?.userId ?? data?.id ?? data?.patientId ?? null;
    return id != null && id !== "" ? String(id) : null;
  } catch {
    return null;
  }
};

/** Account id for user APIs (not patientId). */
export const getUserAccountId = () => {
  const raw =
    localStorage.getItem("userData") || sessionStorage.getItem("userData");
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    const id = data?.userId ?? data?.id ?? null;
    return id != null && id !== "" ? String(id) : null;
  } catch {
    return null;
  }
};

const storageKey = (userId) => `${STORAGE_PREFIX}_${userId}`;

export const getUserPersonalInterestsFromSession = () => {
  const raw =
    localStorage.getItem("userData") || sessionStorage.getItem("userData");
  if (!raw) return [];
  try {
    const data = JSON.parse(raw);
    return extractUserPersonalInterestsArray(data);
  } catch {
    return [];
  }
};

export const getHealthInterests = (userId) => {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.interestIds) ? parsed.interestIds : [];
  } catch {
    return [];
  }
};

/** Merge stored ids + login `userPersonalInterests`, mapped to loaded categories. */
export const resolveHealthInterestSelection = (userId, categories = []) => {
  const validIds = new Set(
    (categories || []).map((cat) => String(cat?.id ?? "")).filter(Boolean)
  );

  const stored = getHealthInterests(userId).filter((id) =>
    validIds.size > 0 ? validIds.has(id) : true
  );

  const personal = getUserPersonalInterestsFromSession();
  const fromLogin =
    categories.length > 0
      ? mapPersonalInterestsToCategoryIds(personal, categories)
      : parseUserHealthInterestIds({ data: personal });

  const merged = Array.from(new Set(stored.concat(fromLogin)));
  if (validIds.size > 0) {
    return merged.filter((id) => validIds.has(id));
  }
  return merged;
};

/** True when the user already has interests from profile/login (skip onboarding modal). */
export const hasUserSelectedHealthInterests = (userId) => {
  if (!userId) return false;
  if (isHealthInterestsComplete(userId)) return true;

  const personal = getUserPersonalInterestsFromSession();
  if (personal.length > 0) {
    return parseUserHealthInterestIds({ data: personal }).length > 0;
  }

  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    const ids = Array.isArray(parsed?.interestIds) ? parsed.interestIds : [];
    return ids.length > 0 && parsed?.source === "login";
  } catch {
    return false;
  }
};

/** Persist login response interests; marks complete when server already has selections. */
export const hydrateHealthInterestsFromLoginPayload = (payloadData, userId) => {
  if (!userId || !payloadData) return;

  const personal = extractUserPersonalInterestsArray(payloadData);
  if (!personal.length) return;

  const interestIds = parseUserHealthInterestIds({ data: personal });
  if (!interestIds.length) return;

  const existing = getHealthInterests(userId);
  if (existing.length > 0 && isHealthInterestsComplete(userId)) return;

  const now = new Date().toISOString();
  localStorage.setItem(
    storageKey(userId),
    JSON.stringify({
      interestIds,
      completedAt: now,
      updatedAt: now,
      source: "login",
    })
  );
};

export const isHealthInterestsComplete = (userId) => {
  if (!userId) return false;
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return Boolean(parsed?.completedAt);
  } catch {
    return false;
  }
};

export const saveHealthInterests = (userId, interestIds) => {
  if (!userId) return;
  localStorage.setItem(
    storageKey(userId),
    JSON.stringify({
      interestIds: interestIds || [],
      completedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    })
  );
  sessionStorage.removeItem(HEALTH_INTERESTS_SESSION_DISMISS_KEY);
};

export const isHealthInterestsDismissedThisSession = () =>
  sessionStorage.getItem(HEALTH_INTERESTS_SESSION_DISMISS_KEY) === "1";

export const dismissHealthInterestsForSession = () => {
  sessionStorage.setItem(HEALTH_INTERESTS_SESSION_DISMISS_KEY, "1");
};

export const clearHealthInterestsSessionDismiss = () => {
  sessionStorage.removeItem(HEALTH_INTERESTS_SESSION_DISMISS_KEY);
};

export const shouldForceHealthInterestsScreen = () => {
  const userId = getCurrentUserId();
  if (!userId) return false;
  if (hasUserSelectedHealthInterests(userId)) return false;
  if (isHealthInterestsDismissedThisSession()) return false;
  return true;
};

export const resolvePostAuthPath = () => "/dashboard";
